import { TextInput, Box, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useState, useEffect } from 'react';
import { getSocialByCode, extractUsernameFromUrl, getUrlPrefix } from '../../../services/socials';
import styles from "./SocialInput.module.css";

/**
 * SocialInput - Input component for social media URLs
 * Shows the fixed URL prefix on the left and only stores the username/account ID
 */
export const SocialInput = ({
  label,
  showLabel = true,
  description,
  error,
  required,
  value = '',
  onChange,
  onBlur,
  placeholder,
  socialCode,
  disabled,
  renderAs = 'prefixed',
  // stackPrefixOnMobile is always true; prop removed
}) => {
  const isMobile = useMediaQuery('(max-width: 48em)');
  const [localValue, setLocalValue] = useState('');
  const social = getSocialByCode(socialCode);

  // Sync with external value changes
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleInputChange = (event) => {
    let inputValue = event.target.value;

    // If user pastes a full URL, extract the username
    if (inputValue && social && (inputValue.includes('http') || inputValue.includes('www'))) {
      inputValue = extractUsernameFromUrl(inputValue, socialCode);
    }

    setLocalValue(inputValue);
    onChange?.(inputValue);
  };

  const handleBlur = (event) => {
    let cleanedValue = localValue;

    // Clean up any remaining URL parts
    if (cleanedValue && social) {
      cleanedValue = extractUsernameFromUrl(cleanedValue, socialCode);

      // Update if cleaned value is different
      if (cleanedValue !== localValue) {
        setLocalValue(cleanedValue);
        onChange?.(cleanedValue);
      }
    }

    onBlur?.(event);
  };

  const urlPrefix = social ? getUrlPrefix(socialCode) : '';
  const IconComponent = social ? social.getIcon() : null;
  // 'plain' drops the prefix box and renders a normal text field. The URL cleanup
  // in handleInputChange and handleBlur stays in place either way.
  const showPrefix = renderAs !== 'plain' && Boolean(urlPrefix);
  const shouldStackPrefix = showPrefix && isMobile;
  const inputStyles = shouldStackPrefix
    ? {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderTop: 'none',
      }
    : showPrefix
      ? {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          borderLeft: 'none',
        }
      : undefined;

  return (
    <Box className={styles.container}>
      {showLabel && label && (
        <Text size="sm" fw={500} mb={4}>
          {label}
          {required && <span style={{ color: 'var(--mantine-color-red-6)' }}> *</span>}
        </Text>
      )}

      {description && (
        <Text size="xs" c="dimmed" mb={4}>
          {description}
        </Text>
      )}

      <Box
        className={`${styles.wrapper} ${error ? styles.wrapperError : ''} ${shouldStackPrefix ? styles.wrapperStacked : ''} ${disabled ? styles.wrapperDisabled : ''}`}
      >
        {showPrefix && (
          <Box className={`${styles.prefix} ${shouldStackPrefix ? styles.prefixStacked : ''}`}>
            {IconComponent && (
              <IconComponent size={16} className={styles.icon} />
            )}
            <Text size="sm" c="dimmed" className={styles.prefixText}>
              {urlPrefix}
            </Text>
          </Box>
        )}

        <TextInput
          value={localValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder={placeholder || 'username'}
          error={error}
          disabled={disabled}
          className={`${styles.field} ${shouldStackPrefix ? styles.fieldStacked : ''}`}
          styles={{
            root: { flex: 1, width: '100%' },
            input: inputStyles,
          }}
        />
      </Box>

      {error && typeof error === 'string' && (
        <Text size="xs" c="red" mt={4}>
          {error}
        </Text>
      )}
    </Box>
  );
};
